import { Badge, Tooltip } from '@/components/ui';
import { statusLabel } from '@/lib/xero';
import type { Product } from '@/lib/types';

/**
 * Where a product's Xero Item stands (owner 2026-10-28: products sync two-way
 * with Xero Items).
 *
 * ⚠️ **`xeroSync` ABSENT is an older API and renders NOTHING** — not "—", not
 * "Not in Xero". `status: null` is the real "never pushed" answer and renders
 * the em dash.
 *
 * `synced` borrows `statusLabel('synced')` from `@/lib/xero`, so this page says
 * "In Xero" in the same word every other Xero surface does.
 *
 * `inlineError` is for the sub-`md` cards: a Tooltip shows nothing on touch, so
 * the refusal is written out beside the badge there instead.
 */
export function ProductXeroBadge({
  product,
  inlineError = false,
}: {
  product: Product;
  inlineError?: boolean;
}) {
  const sync = product.xeroSync;
  if (sync === undefined) return null;

  switch (sync.status) {
    case 'synced':
      return (
        <Badge tone="success" variant="outline">
          {statusLabel('synced')}
        </Badge>
      );
    case 'pending':
      return (
        <Badge tone="info" variant="outline">
          Pending
        </Badge>
      );
    case 'failed': {
      const badge = (
        <Badge tone="danger" variant="soft">
          Xero failed
        </Badge>
      );
      const error = sync.error?.trim() ?? '';
      if (error === '') return badge;
      if (inlineError) {
        return (
          <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
            {badge}
            <span className="text-2xs text-danger [overflow-wrap:anywhere]">{error}</span>
          </span>
        );
      }
      return (
        <Tooltip label={error}>
          <span tabIndex={0} className="inline-flex rounded-sm">
            {badge}
          </span>
        </Tooltip>
      );
    }
    default:
      return <span className="text-xs text-fg-subtle">—</span>;
  }
}
